/* eslint-disable @typescript-eslint/require-await */
/* eslint-disable @typescript-eslint/no-misused-promises */
/**
 * @module DataChannels
 */

import { Subject, Subscription, filter } from "rxjs";
import type { Bubble } from "./bubble.model";
import type { Contact } from "./contact.model";
import { RBEvent } from "./event.model";
import { MainService } from "../services";
import { DATA_CHANNEL_SVC } from "../services/dataChannel";
import type { JingleSession } from "../libs/strophe/strophe.jingle.session";
import type { Branded } from "../utils/branded";
import type { User } from "./user.model";
import { EventableService, makeServiceEvent, RbServiceEvent } from "../services/service";
import type { DataChannelService_RB } from "../services/dataChannel/dataChannel.service";

/**
 * DataChannel status
 */
export enum DataChannelStatus {
    DISCONNECTED = 'disconnected',
    PROPOSED = 'proposed',
    INVITED = 'invited',
    REFUSED = 'refused',
    CONNECTING = 'connecting',
    CONNECTED = 'connected',
    CLOSED = 'closed'
}

/**
 * @eventProperty
 * `DataChannel` specific events
 * Can listen to it via `dataChannel.subscribe()` API
 */
export enum DataChannelEvents {

    /**
     * @eventProperty
     * This `RBEvent` is sent when the dataChannel connection status is modified
     * **Event Data**
     * - **status**: The dataChannel connection status
     */
    ON_STATUS_CHANGED = 'ON_STATUS_CHANGED',

    /**
     * @eventProperty
     * This `RBEvent` is sent when a message is received in a dataChannel
     * **Event Data**
     * - **message**: The message (any)
     */
    ON_MESSAGE_RECEIVED = 'ON_MESSAGE_RECEIVED',

    /**
     * @eventProperty
     * This `RBEvent` is sent a new subscribtion is added to a bubble dataChannel
     * **Event Data**
     * - **user**: The added user subscribtion
     */
    ON_SUBSCRIPTION_ADDED = 'ON_SUBSCRIPTION_ADDED',

    /**
     * @eventProperty
     * This `RBEvent` is sent a subscribtion is removed from a bubble dataChannel
     * **Event Data**
     * - **user**: The removed user subscribtion
     */
    ON_SUBSCRIPTION_REMOVED = 'ON_SUBSCRIPTION_REMOVED'
}

export interface IDataChannelEvents {
    /**
     * This event is sent when the data channel connection status is modified
     */
    ON_STATUS_CHANGED: DataChannelStatus;

    /**
     * This event is sent when a message is received in a data channel
     */
    ON_MESSAGE_RECEIVED: { message: unknown; };

    /**
     * This event is sent when a subscription is added to a bubble data channel
     */
    ON_SUBSCRIPTION_ADDED: User;

    /**
     * This even is sent when a subscription is removed from a bubble data channel
     */
    ON_SUBSCRIPTION_REMOVED: User;
}

export type DataChannelID = Branded<string, 'dataChannelId'>;

export interface DataChannel extends EventableService<IDataChannelEvents> {

    /**
     * DataChannel identifier
     * @readonly
     */
    id: DataChannelID;

    /**
     * DataChannel description string
     * @readonly
     */
    description?: string;

    /**
     * DataChannel publisher
     * @readonly
     */
    publisher?: Contact;

    /**
     * DataChannel bubble
     * @readonly
     */
    bubble?: Bubble;

    /**
     * DataChannel status
     * @readonly
     */
    status?: DataChannelStatus;

    /**
     * Leave a dataChannel
     */
    leave(): Promise<DataChannel>;

    /**
     * Accept a dataChannel
     */
    accept(): Promise<DataChannel>;

    /**
     * Refuse a dataChannel
     */
    refuse(): Promise<DataChannel>;

    /**
     * Delete a dataChannel
     */
    delete(): Promise<void>;

    /**
     * Send data on connected dataChannel
     * @param data - the data to send
     */
    sendMessage(data: any): void;

    /**
     * Send file on connected dataChannel
     * @param file - the file to send
     * @internal
     */
    sendFile(file: File): void;

    /**
    * Subscribe to updates on the current dataChannel (all events are of RBEvent type);
    * @param handler - The call-back function that will be subscribed to the RxJS subject
    * @param filterEvent - a list of event names that the user wants to receive when subscribing (used a filter)
    */
    subscribe(handler: (event: RBEvent<DataChannelEvents>) => any, filterEvent?: string | string[]): Subscription;
}

/**
 * @internal
 */
export class DataChannel_RB implements DataChannel {

    public id: DataChannelID;
    public name?: string;
    public description?: string;
    public ressourceId?: string;
    public type?: 'p2p' | 'janus';
    public bubble?: Bubble;
    public publisher?: Contact;
    public publisherFullJid?: string;
    public sessionId?: string;
    public session?: JingleSession;
    public affiliation?: string;

    public rtcDataChannel: RTCDataChannel;

    public _status: DataChannelStatus = DataChannelStatus.DISCONNECTED;
    public readonly _statusSubject = new Subject<DataChannelStatus>();

    private chunkLength: number = 1000;
    private readonly rxSubject = new Subject<RbServiceEvent<IDataChannelEvents>>();
    private readonly dataChannelService: DataChannelService_RB;
    public static create(): DataChannel_RB { return new DataChannel_RB(); }

    private constructor() {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.dataChannelService = MainService.getInstance().inject<DataChannelService_RB>(DATA_CHANNEL_SVC)!;
    }

    public get status(): DataChannelStatus { return this._status; }
    public set status(status: DataChannelStatus) { this._status = status; this.sendEvent(DataChannelEvents.ON_STATUS_CHANGED, status); }

    public events<const EventNames extends keyof IDataChannelEvents>(...eventNames: EventNames[]) {
        return makeServiceEvent(this.rxSubject, ...eventNames);
    }

    public subscribe(handler: (event: RBEvent<DataChannelEvents>) => any, eventNames?: string | string[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event) => {
                if (Array.isArray(eventNames)) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;
        return observable.subscribe((event) => {
            try { handler(event as RBEvent<DataChannelEvents>); } catch { /* empty */ }
        });
    }

    public sendEvent<const EventName extends keyof IDataChannelEvents>(eventName: EventName, data: IDataChannelEvents[EventName]): void {
        this.rxSubject.next(RBEvent.create(eventName, data));
    }

    public async leave(): Promise<DataChannel> {
        await this.dataChannelService.leaveDataChannel(this);
        return this;
    }

    public async accept(): Promise<DataChannel> {
        await this.dataChannelService.acceptDataChannel(this);
        return this;
    }

    public async refuse(): Promise<DataChannel> {
        await this.dataChannelService.refuseDataChannel(this);
        return this;
    }

    public async delete(): Promise<void> {
        await this.dataChannelService.deleteDataChannel(this);
    }

    public sendMessage(data: any): void {
        if (!this.rtcDataChannel || this.rtcDataChannel.readyState !== 'open') throw new Error('DataChannel not ready');
        this.rtcDataChannel.send(data);
    }

    public async sendFile(file: File): Promise<void> {
        if (!this.rtcDataChannel || this.rtcDataChannel.readyState !== 'open') throw new Error('DataChannel not ready');
        const reader = new window.FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => { this.onReadAsDataURL(event, null); };
    }

    private onReadAsDataURL(event: any, text: any): void {
        const data: any = {};
        if (event) text = event.target.result;
        if (text.length > this.chunkLength) data.message = text.slice(0, this.chunkLength);
        else { data.message = text; data.last = true; }
        this.rtcDataChannel.send(data);
        const remainingDataURL = text.slice(data.message.length);
        if (remainingDataURL.length) setTimeout(() => { this.onReadAsDataURL(null, remainingDataURL); }, 500);
    }

    public toJSON(): any {
        const json: any = { id: this.id, name: this.name, description: this.description, ressourceId: this.ressourceId };
        if (this.publisher) json.publisher = this.publisher.dbId;
        json.status = this.status;
        return json;
    }

}
